import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { setCurrentPageTitle, setCurrentRoute } from "../store/layoutUI";

const LANDING_PAGE_STYLES = {
    shell: {
        display: "flex",
        flexDirection: "column",
        gap: "18px",
        padding: "4px 2px 10px 2px"
    },
    hero: {
        position: "relative",
        overflow: "hidden",
        borderRadius: "24px",
        padding: "26px 28px",
        border: "1px solid rgba(191, 219, 254, 0.9)",
        background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 52%, #f8fafc 100%)",
        boxShadow: "0 18px 40px rgba(37, 99, 235, 0.08)"
    },
    heroGlow: {
        position: "absolute",
        top: "-80px",
        right: "-60px",
        width: "220px",
        height: "220px",
        borderRadius: "999px",
        background: "radial-gradient(circle, rgba(59,130,246,0.12) 0%, rgba(59,130,246,0) 72%)",
        pointerEvents: "none"
    },
    heroRow: {
        position: "relative",
        zIndex: 1,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "18px",
        flexWrap: "wrap"
    },
    heroCopy: {
        minWidth: "280px",
        flex: 1
    },
    eyebrow: {
        fontSize: "11px",
        fontWeight: 800,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: "#2563eb",
        marginBottom: "10px"
    },
    title: {
        fontSize: "32px",
        lineHeight: 1.08,
        fontWeight: 800,
        color: "#0f172a",
        margin: "0 0 12px 0",
        maxWidth: "760px"
    },
    subtitle: {
        fontSize: "15px",
        lineHeight: 1.65,
        color: "#475569",
        margin: 0,
        maxWidth: "760px"
    },
    badgeWrap: {
        display: "flex",
        gap: "10px",
        flexWrap: "wrap",
        justifyContent: "flex-end"
    },
    badge: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: "10px 14px",
        borderRadius: "999px",
        backgroundColor: "#ffffff",
        border: "1px solid rgba(191, 219, 254, 0.95)",
        color: "#1d4ed8",
        fontSize: "12px",
        fontWeight: 700,
        boxShadow: "0 10px 24px rgba(37, 99, 235, 0.08)"
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "1.35fr 1fr",
        gap: "18px"
    },
    panel: {
        backgroundColor: "#ffffff",
        border: "1px solid rgba(226, 232, 240, 1)",
        borderRadius: "22px",
        padding: "22px",
        boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)"
    },
    sectionLabel: {
        fontSize: "11px",
        fontWeight: 800,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "#64748b",
        marginBottom: "10px"
    },
    sectionTitle: {
        fontSize: "22px",
        fontWeight: 800,
        color: "#0f172a",
        margin: "0 0 10px 0"
    },
    sectionText: {
        fontSize: "14px",
        lineHeight: 1.7,
        color: "#475569",
        margin: 0
    },
    quickGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "14px",
        marginTop: "18px"
    },
    quickCard: {
        display: "block",
        textDecoration: "none",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
        border: "1px solid rgba(219, 234, 254, 0.95)",
        borderRadius: "18px",
        padding: "18px 18px 16px 18px",
        boxShadow: "0 10px 24px rgba(37, 99, 235, 0.06)",
        minHeight: "150px"
    },
    quickIcon: {
        width: "42px",
        height: "42px",
        borderRadius: "14px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
        color: "#ffffff",
        fontSize: "16px",
        marginBottom: "16px",
        boxShadow: "0 10px 20px rgba(37, 99, 235, 0.18)"
    },
    quickTitle: {
        fontSize: "16px",
        fontWeight: 800,
        color: "#0f172a",
        marginBottom: "8px"
    },
    quickText: {
        fontSize: "13px",
        lineHeight: 1.55,
        color: "#475569",
        marginBottom: "12px"
    },
    quickLink: {
        fontSize: "12px",
        fontWeight: 800,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#2563eb"
    },
    checklist: {
        display: "grid",
        gap: "12px",
        marginTop: "16px"
    },
    checklistRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: "12px 14px",
        borderRadius: "16px",
        background: "#f8fafc",
        border: "1px solid rgba(226, 232, 240, 1)"
    },
    checklistDot: {
        width: "28px",
        height: "28px",
        borderRadius: "999px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#dbeafe",
        color: "#1d4ed8",
        fontSize: "12px",
        fontWeight: 800,
        flexShrink: 0,
        marginTop: "1px"
    },
    checklistTitle: {
        fontSize: "14px",
        fontWeight: 700,
        color: "#0f172a",
        marginBottom: "3px"
    },
    checklistText: {
        fontSize: "13px",
        lineHeight: 1.55,
        color: "#64748b"
    }
};

export default function LandingPage() {
    const usrType = localStorage.getItem("userType");
    const dispatch = useDispatch();

    const route = [{ name: "Home", url: "" }];

    useEffect(() => {
        dispatch(setCurrentPageTitle("Home"));
        dispatch(setCurrentRoute(route));
    }, []);

    const isCandidate = usrType === "candidate";
    const portalLabel = isCandidate ? "Candidate Workspace" : "Partner Workspace";

    const quickActions = isCandidate
        ? [
            {
                title: "Continue Onboarding",
                description: "Pick up your onboarding tasks, complete required forms, and move your application forward.",
                href: "/Onboarding",
                icon: "fa fa-rocket"
            },
            {
                title: "Contract Documents",
                description: "Review and complete contract-related documents in one place.",
                href: "/ContractDocs",
                icon: "fa fa-file-text-o"
            },
            {
                title: "Background Checks",
                description: "Track pending checks and submit any remaining items quickly.",
                href: "/BackgroundChecks",
                icon: "fa fa-shield"
            },
            {
                title: "Reporting Details",
                description: "Update reporting info and keep your project details current.",
                href: "/ReportingDetails",
                icon: "fa fa-address-card-o"
            }
        ]
        : [
            {
                title: "Partner Candidates",
                description: "Review candidate activity, progress, and onboarding readiness from one hub.",
                href: "/PartnerCandidates",
                icon: "fa fa-users"
            },
            {
                title: "Partner Invoices",
                description: "Check invoice workflows, status, and submissions without leaving the portal.",
                href: "/PartnerInvoices",
                icon: "fa fa-money"
            },
            {
                title: "Manage Users",
                description: "Maintain partner user access, permissions, and visibility across the account.",
                href: "/ManagePartnerUsers",
                icon: "fa fa-user-circle-o"
            },
            {
                title: "Compliance Reports",
                description: "Open compliance reporting tools and monitor key partner obligations.",
                href: "/ComplianceRpt",
                icon: "fa fa-line-chart"
            }
        ];

    const checklistItems = isCandidate
        ? [
            {
                title: "Start with the left navigation",
                text: "Use the menu to move through onboarding, contracts, reporting, and required follow-up tasks."
            },
            {
                title: "Complete sections in order",
                text: "Finishing each step in sequence helps avoid missing dependencies later in the process."
            },
            {
                title: "Return here anytime",
                text: "This page gives you a quick launch point into the areas you will use most often."
            }
        ]
        : [
            {
                title: "Open your operational tools",
                text: "Use the quick actions below to jump into candidates, invoices, user management, and reporting."
            },
            {
                title: "Keep account details current",
                text: "Updated contact, compliance, and user information keeps your team moving smoothly."
            },
            {
                title: "Use this page as your launchpad",
                text: "The landing experience is designed to bring your most common partner workflows within easy reach."
            }
        ];

    return (
        <React.Fragment>
            <style>{`
                @media (max-width: 992px) {
                    .landing-grid {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>

            <div style={LANDING_PAGE_STYLES.shell}>
                <div style={LANDING_PAGE_STYLES.hero}>
                    <div style={LANDING_PAGE_STYLES.heroGlow}></div>
                    <div style={LANDING_PAGE_STYLES.heroRow}>
                        <div style={LANDING_PAGE_STYLES.heroCopy}>
                            <div style={LANDING_PAGE_STYLES.eyebrow}>{portalLabel}</div>
                            <h1 style={LANDING_PAGE_STYLES.title}>
                                Welcome to IPCS Human Resources, Onboarding, and Contract Management
                            </h1>
                            <p style={LANDING_PAGE_STYLES.subtitle}>
                                Move through the portal with less friction. Your most important actions are grouped below so you can get where you need to go quickly.
                            </p>
                        </div>

                        <div style={LANDING_PAGE_STYLES.badgeWrap}>
                            <span style={LANDING_PAGE_STYLES.badge}>
                                <i className="fa fa-compass" aria-hidden="true"></i>
                                Guided Start
                            </span>
                            <span style={LANDING_PAGE_STYLES.badge}>
                                <i className="fa fa-bolt" aria-hidden="true"></i>
                                Quick Actions
                            </span>
                        </div>
                    </div>
                </div>

                <div className="landing-grid" style={LANDING_PAGE_STYLES.grid}>
                    <div style={LANDING_PAGE_STYLES.panel}>
                        <div style={LANDING_PAGE_STYLES.sectionLabel}>Quick Actions</div>
                        <h2 style={LANDING_PAGE_STYLES.sectionTitle}>Jump straight into your work</h2>
                        <p style={LANDING_PAGE_STYLES.sectionText}>
                            Open the tasks and areas you are most likely to use next without hunting through the entire menu.
                        </p>

                        <div style={LANDING_PAGE_STYLES.quickGrid}>
                            {quickActions.map((action) => (
                                <a key={action.title} href={action.href} style={LANDING_PAGE_STYLES.quickCard}>
                                    <span style={LANDING_PAGE_STYLES.quickIcon}>
                                        <i className={action.icon} aria-hidden="true"></i>
                                    </span>
                                    <div style={LANDING_PAGE_STYLES.quickTitle}>{action.title}</div>
                                    <div style={LANDING_PAGE_STYLES.quickText}>{action.description}</div>
                                    <div style={LANDING_PAGE_STYLES.quickLink}>Open Section</div>
                                </a>
                            ))}
                        </div>
                    </div>

                    <div style={LANDING_PAGE_STYLES.panel}>
                        <div style={LANDING_PAGE_STYLES.sectionLabel}>Getting Started</div>
                        <h2 style={LANDING_PAGE_STYLES.sectionTitle}>A cleaner way to move through the portal</h2>
                        <p style={LANDING_PAGE_STYLES.sectionText}>
                            This workspace is designed to keep the next best step obvious while still giving you quick access to the rest of the system.
                        </p>

                        <div style={LANDING_PAGE_STYLES.checklist}>
                            {checklistItems.map((item, index) => (
                                <div key={item.title} style={LANDING_PAGE_STYLES.checklistRow}>
                                    <div style={LANDING_PAGE_STYLES.checklistDot}>{index + 1}</div>
                                    <div>
                                        <div style={LANDING_PAGE_STYLES.checklistTitle}>{item.title}</div>
                                        <div style={LANDING_PAGE_STYLES.checklistText}>{item.text}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}
